import type {ReactElement} from 'react';
import {Button, Size, Type} from '@Components/button';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import type {CreditBenefitsPanelProps} from '@Components/checkout-flow/components/credit-benefits-panel/credit-benefits-panel.types';
import {CREDIT_BENEFITS_FLASH_GRAPHIC, PURCHASE_FOOTER_BLUR_LAYERS, getCreditBenefits} from '@Components/checkout-flow/components/credit-benefits-panel/credit-benefits-panel.types';
import {getAssetUrl} from '@Utils/s3.util';
import {CreditBenefitItem} from '@Components/checkout-flow/components/credit-benefit-item/credit-benefit-item';
import styles from './credit-benefits-panel.module.scss';

export function CreditBenefitsPanel({isCompact, selectedCreditPack, formattedPrice, isPurchasing, onBack, onClose, onContinue}: CreditBenefitsPanelProps): ReactElement {
  const getNavigationBar = (): ReactElement | null => {
    if (!isCompact) {
      return null;
    }
    return (
      <div className={styles.navigationBar}>
        <Icon
          icon="icon-arrow-left"
          size={IconSize.SIZE_ICON_24}
          type={IconType.GHOST_UNCOLORED}
          shape={IconShape.SQUARE}
          className={styles.navigationIcon}
          onClick={onBack}
          isUsedAsButton
          ariaLabel={window.i18next.t('pmwjs_back')}
        />
        <Icon
          icon="icon-close"
          size={IconSize.SIZE_ICON_24}
          type={IconType.GHOST_UNCOLORED}
          shape={IconShape.SQUARE}
          className={styles.navigationIcon}
          onClick={onClose}
          isUsedAsButton
          ariaLabel={window.i18next.t('pmwjs_close')}
        />
      </div>
    );
  };

  const getPurchaseFooter = (): ReactElement | null => {
    if (!isCompact || selectedCreditPack === null) {
      return null;
    }
    return (
      <div className={styles.footerRegion}>
        <div className={styles.purchaseFooterBlur} aria-hidden="true">
          {PURCHASE_FOOTER_BLUR_LAYERS.map((layerClass) => {
            return <div key={layerClass} className={styles[layerClass]} />;
          })}
        </div>
        <div className={styles.purchaseFooter}>
          <div className={styles.purchaseSummary}>
            <Text val={selectedCreditPack.name} size={TextSize.SM} className="ct-neutral-primary-on-dark" />
            <Text val={formattedPrice} size={TextSize.LG} bold className="ct-neutral-primary-on-dark" />
          </div>
          <Button type={Type.PRIMARY} size={Size.MEDIUM} text={window.i18next.t('pmwjs_continue')} isLoading={isPurchasing} disabled={isPurchasing} onClick={onContinue} />
        </div>
      </div>
    );
  };

  return (
    <div className={`${styles.panel} ${isCompact ? styles.panelCompact : ''}`}>
      {getNavigationBar()}
      <div className={styles.content}>
        <div className={styles.headline}>
          <img className={styles.flashGraphic} src={getAssetUrl(CREDIT_BENEFITS_FLASH_GRAPHIC)} alt="" />
          <Text val={window.i18next.t('pmwjs_what_can_i_use_credits_for')} size={TextSize.LG} bold className={`ct-neutral-primary-on-dark ${styles.headlineText}`} />
        </div>
        <ul className={styles.benefits}>
          {getCreditBenefits().map((benefit) => {
            return <CreditBenefitItem key={benefit.titleKey} benefit={benefit} />;
          })}
        </ul>
      </div>
      {getPurchaseFooter()}
    </div>
  );
}
